/* eslint-disable react/no-danger */
import { Illustration } from '@eventbrite/eds-illustration';
import { gettext } from '@eventbrite/i18n';
import { BoostBenefits } from '@eventbrite/marketing-tools-common';
import React, { useEffect, useState } from 'react';
import {
    ActivationTrialBackground,
    ActivationTrialReadyIcon,
    ButtonsSecondStepProps,
    errorNotification,
    LearnMoreInformation,
    ModalSecondStepButtons,
    onNoThanksClick,
    onShowSecondStepClick,
    SeeAllPlansInformation,
    StatsDisclaimer,
    trackOnOpen,
    TRIAL_FLOW_ITEMS,
} from '../constants/ActivationTrialModal';
import './ActivationModal.scss';
import {
    ModalConfirmStepProps,
    ModalInviteStepProps,
    TrialFlowProps,
} from './types';

export const TrialFlow = (props: TrialFlowProps) => {
    const [showSecondStep, setShowSecondStep] = useState<boolean>(false);
    const [showMessageError, setShowMessageError] = useState<boolean>(false);
    const [messageError, setMessageError] = useState<string>('');

    useEffect(() => {
        trackOnOpen(props.amplitudeSource, props.trialVersion);
    }, [props.amplitudeSource, props.trialVersion]);

    const enableSecondStep = () => {
        props?.setShowSecondStep?.(true);
        setShowSecondStep(true);
    };

    const showError = (message: string) => {
        setMessageError(message);
        setShowMessageError(true);
    };

    const NotificationError = () => {
        return showMessageError ? errorNotification(messageError) : null;
    };

    const ModalInviteStep = (props: ModalInviteStepProps & TrialFlowProps) => {
        const HeadingContent = () => {
            return (
                <div
                    dangerouslySetInnerHTML={{
                        __html:
                            props?.firstStepHeadingContent ||
                            gettext(
                                "Make the most of your event marketing with <span class='emphasis'>Eventbrite Boost<span>",
                            ).toString(),
                    }}
                />
            );
        };

        const handleCTAButtonClick = () => {
            props?.onCTAButtonClick?.();
            onShowSecondStepClick(
                enableSecondStep,
                showError,
                props.trialVersion,
            );
        };

        const handleDismiss = () => {
            props?.onDismiss?.();
            onNoThanksClick(props.closeModal);
        };

        return (
            <div className="boost-activation-modal__content_step_1">
                <BoostBenefits
                    items={props?.trialFlowItems || TRIAL_FLOW_ITEMS}
                    headingContent={props?.headingContent || <HeadingContent />}
                    statsContent={props?.statsContent}
                    bottomBarContent={
                        props?.bottomBarContent ? (
                            props?.bottomBarContent
                        ) : props?.hideBottomBarContent ? undefined : props?.disclaimerContent ? (
                            <StatsDisclaimer
                                disclaimerContent={props?.disclaimerContent}
                            />
                        ) : (
                            <LearnMoreInformation
                                learnMoreInformationText={
                                    props?.learnMoreInformationText
                                }
                            />
                        )
                    }
                    declineButtonText={gettext('%(text)s', {
                        text: props?.declineButtonText || 'No thanks',
                    })}
                    onDeclineButtonClick={handleDismiss}
                    CTAButtonText={gettext('%(text)s', {
                        text:
                            props?.ctaButtonText || 'Start a free 14-day trial',
                    })}
                    onCTAButtonClick={handleCTAButtonClick}
                    static={props?.static}
                    staticIllustration={props?.staticIllustration}
                />
            </div>
        );
    };

    const ModalConfirmStep = (
        props: ButtonsSecondStepProps & ModalConfirmStepProps,
    ) => (
        <>
            <div className="boost-activation-modal__content_step_2">
                <div className="boost-activation-modal__background">
                    <Illustration
                        type={<ActivationTrialBackground />}
                        height="100%"
                        width="100%"
                    />
                </div>

                <>
                    {props?.customContent ? (
                        <>
                            {props?.customContent}
                            <div
                                className={
                                    props?.buttonsPositionClassName ||
                                    'boost-activation-modal__buttons_position'
                                }
                            >
                                <div className="boost-activation-modal-second-step-button">
                                    <ModalSecondStepButtons {...props} />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="boost-activation-modal__icon_position eds-align--center-horizontal">
                                {ActivationTrialReadyIcon}
                            </div>
                            <div className="eds-l-mar-top-6 eds-l-pad-top-2 eds-align-center eds-text-hl">
                                <div className="eds-align--center eds-text-color--control--active">
                                    {gettext('Ready. Set. Grow!')}
                                </div>
                                <div className="eds-align--center eds-text-color--secondary-brand">
                                    {gettext(
                                        'Your 14-day free trial starts now',
                                    )}
                                </div>
                            </div>
                            <div className="boost-activation-modal__buttons_position">
                                <div className="boost-activation-help-text">
                                    <SeeAllPlansInformation />
                                </div>
                                <div className="boost-activation-help-button"></div>
                                <div className="boost-activation-help-button">
                                    <ModalSecondStepButtons {...props} />
                                </div>
                            </div>
                        </>
                    )}
                </>
            </div>
        </>
    );

    const firstStep = (
        <>
            <NotificationError />
            <ModalInviteStep
                trialFlowItems={props?.trialFlowItems}
                enableSecondStep={() => enableSecondStep()}
                showError={showError}
                closeModal={() => props?.onCloseModal?.(showSecondStep)}
                showMessageError={showMessageError}
                headingContent={props?.headingContent}
                static={props?.static}
                staticIllustration={props?.staticIllustration}
                declineButtonText={props?.declineButtonText}
                ctaButtonText={props?.ctaButtonText}
                hideBottomBarContent={props?.hideBottomBarContent}
                onCTAButtonClick={props?.onCTAButtonClick}
                onDismiss={props?.onDismiss}
                learnMoreInformationText={props?.learnMoreInformationText}
                firstStepHeadingContent={props?.firstStepHeadingContent}
                trialVersion={props.trialVersion}
                disclaimerContent={props?.disclaimerContent}
                statsContent={props?.statsContent}
            />
        </>
    );
    const secondStep = (
        <ModalConfirmStep
            closeModal={() => props?.onCloseModal?.(showSecondStep)}
            getStartedRedirect={props?.getStartedRedirect}
            onGetStartedClick={props?.onGetStartedClick}
            customContent={props?.customSecondStepContent}
            secondStepButtonText={props?.secondStepButtonText}
            buttonsPositionClassName={props?.buttonsPositionClassName}
            reverseBottomButtons={props?.reverseBottomButtons}
        />
    );
    return showSecondStep ? secondStep : firstStep;
};
